import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PenLine, Loader2, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { API, pollJob, errMsg } from "@/lib/api";

const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success("Copiato"));

const Block = ({ label, text, testid }) => text ? (
  <div className="border border-[#E8E8E4] p-4" data-testid={testid}>
    <div className="flex justify-between items-center mb-2"><span className="label !text-[9px]">{label}</span>
      <button onClick={() => copy(text)} data-testid={`${testid}-copy`} className="text-[#5C5C58] hover:text-[#2A4B3C]"><Copy className="w-3.5 h-3.5" /></button></div>
    <p className="text-sm whitespace-pre-line">{text}</p>
  </div>
) : null;

const Script = ({ rows }) => (
  <div className="border border-[#E8E8E4]" data-testid="caption-script">
    <div className="label !text-[9px] p-4 pb-2">Script scena per scena</div>
    {rows.map((s, i) => (
      <div key={i} className="grid grid-cols-[70px_1fr] gap-3 px-4 py-3 border-t border-[#E8E8E4] text-sm">
        <div><div className="font-mono-num text-[#C05C3D]">#{s.scena}</div><div className="text-xs text-[#5C5C58]">{s.durata}</div></div>
        <div className="space-y-1">
          <div><span className="text-xs text-[#5C5C58]">Visual: </span>{s.visual}</div>
          {s.testo_schermo && <div><span className="text-xs text-[#5C5C58]">Testo a schermo: </span><b>{s.testo_schermo}</b></div>}
          {s.voiceover && <div><span className="text-xs text-[#5C5C58]">Voce: </span><i>{s.voiceover}</i></div>}
        </div>
      </div>
    ))}
  </div>
);

const Result = ({ d }) => (
  <div className="space-y-3" data-testid="caption-result">
    {d.differenzia_piattaforme === false ? (
      <Block testid="caption-ig" label="Caption unica (Instagram e Facebook)" text={d.caption_instagram} />
    ) : (
      <>
        <Block testid="caption-ig" label="Caption Instagram" text={d.caption_instagram} />
        <Block testid="caption-fb" label="Caption Facebook" text={d.caption_facebook} />
      </>
    )}
    {d.motivo_differenziazione && <p className="text-xs text-[#5C5C58] border-l-2 border-[#C9A227] pl-3" data-testid="caption-diff-reason"><b>{d.differenzia_piattaforme === false ? "Stesso testo su entrambe" : "Testi diversi"}:</b> {d.motivo_differenziazione}</p>}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Block testid="caption-cover" label="Testo copertina" text={d.testo_copertina} />
      <Block testid="caption-cta" label="CTA" text={d.cta} />
    </div>
    {d.script?.length > 0 && <Script rows={d.script} />}
    <Block testid="caption-hashtags" label="Hashtag" text={(d.hashtag || []).join(" ")} />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Block testid="caption-first-comment" label="Primo commento da fissare" text={d.primo_commento} />
      <Block testid="caption-story" label="Story di supporto" text={d.story_di_supporto} />
      <Block testid="caption-audio" label="Audio suggerito" text={d.suggerimento_audio} />
      <Block testid="caption-notes" label="Note di produzione" text={d.note_produzione} />
    </div>
  </div>
);

export const CaptionButton = ({ idea, index }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [chars, setChars] = useState(0);
  const cancelled = useRef(false);
  useEffect(() => { cancelled.current = false; return () => { cancelled.current = true; }; }, []);

  const openDialog = async () => {
    setOpen(true);
    if (data) return;
    try {
      const r = await axios.get(`${API}/captions`, { params: { title: idea.titolo } });
      if (r.data.length) setData(r.data[0].data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const generate = async () => {
    setBusy(true); setChars(0);
    try {
      const { data: { job_id } } = await axios.post(`${API}/captions/generate`, { idea });
      const id = await pollJob(job_id, setChars, () => cancelled.current);
      if (!id) return;
      const r = await axios.get(`${API}/captions/${id}`);
      setData(r.data.data);
      toast.success("Caption e script pronti");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button data-testid={`write-caption-btn-${index}`} onClick={openDialog} className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[#2A4B3C] border border-[#2A4B3C] px-3 py-1.5 rounded-full hover:bg-[#2A4B3C] hover:text-white transition-colors">
        <PenLine className="w-3.5 h-3.5" />Scrivi caption & script
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto rounded-none" data-testid="caption-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{idea.titolo}</DialogTitle></DialogHeader>
          <p className="text-xs text-[#5C5C58] -mt-2">{idea.formato} · {idea.piattaforma} · obiettivo {idea.obiettivo}</p>
          <Button data-testid="generate-caption-btn" onClick={generate} disabled={busy} className="rounded-full bg-[#C05C3D] hover:bg-[#A94E32] self-start w-fit">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PenLine className="w-4 h-4 mr-2" />}{busy ? `Scrittura in corso... ${chars} caratteri` : data ? "Riscrivi" : "Genera caption & script"}
          </Button>
          {data ? <Result d={data} /> : !busy && <p className="text-sm text-[#5C5C58]">Claude scriverà caption Instagram e Facebook, script scena per scena, hashtag, CTA e primo commento. Circa 30-60 secondi.</p>}
        </DialogContent>
      </Dialog>
    </>
  );
};
